import React from 'react';
import { User, Award, Briefcase, Zap, CheckCircle2 } from 'lucide-react';
import { portfolioData } from '../data/portfolioData';

export const About: React.FC = () => {
  return (
    <section id="about" className="section about-section">
      <div className="container">
        <div className="section-header">
          <div className="section-badge">
            <User size={14} />
            <span>About Me</span>
          </div>
          <h2 className="section-title">
            Passionate Engineering <span className="text-gradient">Leader & Architect</span>
          </h2>
          <p className="section-subtitle">
            Transforming complex technical challenges into robust, high-performance software solutions.
          </p>
        </div>

        <div className="about-grid">
          <div className="about-bio glass-card">
            <h3 className="bio-title">Who Am I?</h3>
            <p className="bio-text">{portfolioData.intro}</p>
            <p className="bio-text">{portfolioData.description}</p>

            <div className="highlights-wrapper">
              <h4 className="highlights-title">Core Enterprise Milestones</h4>
              <ul className="highlights-list">
                {portfolioData.highlights.map((item, idx) => (
                  <li key={idx}>
                    <CheckCircle2 size={18} className="check-icon" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="about-stats">
            <div className="stat-card glass-card">
              <div className="stat-icon exp-bg">
                <Briefcase size={24} />
              </div>
              <div className="stat-num">{portfolioData.stats.yearsExp}+</div>
              <div className="stat-label">Years Experience</div>
            </div>

            <div className="stat-card glass-card">
              <div className="stat-icon project-bg">
                <Zap size={24} />
              </div>
              <div className="stat-num">{portfolioData.stats.projectsCompleted}+</div>
              <div className="stat-label">Projects Completed</div>
            </div>

            <div className="stat-card glass-card">
              <div className="stat-icon award-bg">
                <Award size={24} />
              </div>
              <div className="stat-num">{portfolioData.stats.companiesWorked}</div>
              <div className="stat-label">Engineering Roles</div>
            </div>

            <div className="stat-card glass-card">
              <div className="stat-icon client-bg">
                <User size={24} />
              </div>
              <div className="stat-num">{portfolioData.stats.satisfiedClients}+</div>
              <div className="stat-label">Global Clients</div>
            </div>

            <div className="hire-banner glass-card">
              <h3>Have a high-scale project in mind?</h3>
              <p>Available for technical leadership, full-stack consulting & high-impact contracts.</p>
              <a href="#contact" className="btn btn-primary">
                Let's Talk
              </a>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .about-section {
          background: var(--bg-secondary);
        }

        .about-grid {
          display: grid;
          grid-template-columns: 1.2fr 0.8fr;
          gap: 2rem;
          align-items: start;
        }

        .about-bio {
          padding: 2.5rem;
        }

        .bio-title {
          font-size: 1.6rem;
          font-weight: 700;
          margin-bottom: 1.25rem;
          color: var(--text-main);
        }

        .bio-text {
          font-size: 1.05rem;
          color: var(--text-muted);
          line-height: 1.75;
          margin-bottom: 1.25rem;
        }

        .highlights-wrapper {
          margin-top: 2rem;
          padding-top: 1.5rem;
          border-top: 1px solid var(--border-color);
        }

        .highlights-title {
          font-size: 1.1rem;
          font-weight: 700;
          color: var(--text-main);
          margin-bottom: 1rem;
        }

        .highlights-list {
          list-style: none;
          display: flex;
          flex-direction: column;
          gap: 0.9rem;
        }

        .highlights-list li {
          display: flex;
          align-items: flex-start;
          gap: 0.75rem;
          font-size: 0.95rem;
          color: var(--text-muted);
          line-height: 1.5;
        }

        .check-icon {
          color: var(--accent-secondary);
          flex-shrink: 0;
          margin-top: 2px;
        }

        .about-stats {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 1.25rem;
        }

        .stat-card {
          padding: 1.75rem 1.25rem;
          text-align: center;
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .stat-icon {
          width: 50px;
          height: 50px;
          border-radius: var(--radius-sm);
          display: flex;
          align-items: center;
          justify-content: center;
          color: #fff;
          margin-bottom: 1rem;
        }

        .exp-bg { background: linear-gradient(135deg, #6366f1, #4f46e5); }
        .project-bg { background: linear-gradient(135deg, #06b6d4, #0891b2); }
        .award-bg { background: linear-gradient(135deg, #ec4899, #db2777); }
        .client-bg { background: linear-gradient(135deg, #10b981, #059669); }

        .stat-num {
          font-size: 2.2rem;
          font-weight: 800;
          color: var(--text-main);
          line-height: 1;
          margin-bottom: 0.35rem;
        }

        .stat-label {
          font-size: 0.85rem;
          color: var(--text-muted);
          font-weight: 500;
        }

        .hire-banner {
          grid-column: span 2;
          padding: 1.75rem;
          background: var(--gradient-glow);
          border: 1px solid var(--border-highlight);
          text-align: center;
        }

        .hire-banner h3 {
          font-size: 1.15rem;
          font-weight: 700;
          margin-bottom: 0.5rem;
        }

        .hire-banner p {
          font-size: 0.9rem;
          color: var(--text-muted);
          margin-bottom: 1.25rem;
        }

        @media (max-width: 992px) {
          .about-grid {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 576px) {
          .about-stats {
            grid-template-columns: 1fr;
          }
          .hire-banner {
            grid-column: span 1;
          }
        }
      `}</style>
    </section>
  );
};
